import React, { useState, useEffect, useCallback } from 'react';
import { ColumnsType } from 'antd/es/table';
import { Input, DatePicker, Button, Badge, Select, Spin } from 'antd';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { notificationController } from 'controllers/notificationController';

import {  getAllProductsAction } from '@app/store/slices/userSlice';
import { useAppDispatch } from '@app/hooks/reduxHooks';

import { BaseSpace } from '@app/components/common/BaseSpace/BaseSpace';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Status } from '@app/components/profile/profileCard/profileFormNav/nav/payments/paymentHistory/Status/Status';
const { RangePicker } = DatePicker;
const { Option } = Select;

export const BasicTable: React.FC = () => {
  const [filters, setFilters] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: ''
  });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [tableData, setTableData] = useState({ data: [], loading: false, total: 0 });
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  let navigate = useNavigate();
  // Fetch data with updated filters and pagination
  const fetchData = useCallback(() => {
    setTableData((prev) => ({ ...prev, loading: true }));

    const params = {
      ...filters,
      current: pagination.current,
      pageSize: pagination.pageSize,
    };

    dispatch(getAllProductsAction(params)).unwrap()
      .then((response) => {
        setTableData({
          data: response.data,
          loading: false,
          total: response.total,
        });
      })
      .catch((error) => {
        notificationController.error({ message: error.message || 'Failed to fetch data' });
        setTableData((prev) => ({ ...prev, loading: false }));
      });
  }, [pagination, dispatch]);



  // Trigger fetch when filters or pagination change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle filter changes
  const handleFilterChange = (filterName: string, value: any) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
  };

  // Handle table pagination change
  const handleTableChange = (paginationInfo) => {
    setPagination({ current: paginationInfo.current, pageSize: paginationInfo.pageSize });
  };

  const columns: ColumnsType<BasicTableRow> = [
    {
      title: 'Name',
      dataIndex: 'name',
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
              <div style={{ padding: 8 }}>
                <Input
                  placeholder="Search First Name"
                  value={selectedKeys[0]}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedKeys(value ? [value] : []);
                    handleFilterChange('name', value); // Update the filter state
                  }}
                  onPressEnter={() => confirm()}
                  style={{ width: 188, marginBottom: 8, display: 'block' }}
                />
                <Button
                  type="primary"
                  onClick={() => confirm()}
                  size="small"
                  style={{ width: 90, marginRight: 8 }}
                >
                  Search
                </Button>
                <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
                  Reset
                </Button>
              </div>
            ),
      // onFilter: (value, record) => record.name.toLowerCase().includes((value as string).toLowerCase()),
      filterIcon: () => <span>🔍</span>,
    },
    {
      title: 'Price',
      dataIndex: 'price',
      render: (_: any, record: any) => {
        if (record.isVariantEnabled) {
          const prices = record.variants.flatMap((v: any) =>
            v.options.map((o: any) => o.sale_price)
          );
          const minPrice = Math.min(...prices);
          const maxPrice = Math.max(...prices);
          return <span>Rs {minPrice} - Rs {maxPrice}</span>;
        }
        return <span>Rs {record.sale_price}</span>;
      },
    },
    {
      title: 'Category',
      dataIndex: 'category',
      render: (category: any) => <span>{category?.name}</span>,
    },
    {
      title: 'Quantity (Size)',
      dataIndex: 'quantity',
      render: (_: any, record: any) => {
        if (record.isVariantEnabled) {
          return (
            <span>
              {record.variants.flatMap((v: any) =>
                v.options.map((o: any) => `${o.quantity} ${o.unit}`)
              ).join(', ')}
            </span>
          );
        }
        return <span>{record.quantity} {record.unit}</span>;
      },
    },
    {
      title: 'Available Quantity',
      dataIndex: 'quantityAvailable',
      render: (_: any, record: any) => {
        if (record.isVariantEnabled) {
          return (
            <span>
              {record.variants.flatMap((v: any) =>
                v.options.map((o: any) => `${o.quantityAvailable}`)
              ).join(', ')}
            </span>
          );
        }
        return <span>{record.quantityAvailable}</span>;
      },
    },
    {
      title: t('common.status'),
      key: 'isActive',
      dataIndex: 'isActive',
      render: (isActive: Boolean) => (
        <Status
          color={isActive ? 'var(--success-color)' : 'var(--error-color)'}
          text={isActive ? 'ACTIVE' : 'INACTIVE'}
        />
      ),
    },
    {
      title: t('tables.actions'),
      dataIndex: 'actions',
      width: '15%',
      render: (text: string, record: any) => {
        return (
          <BaseSpace>
            <BaseButton
              type="ghost"
              onClick={() => {
                navigate('/admin/products/edit/' + record?._id)
                // notificationController.info({ message: t('tables.inviteMessage', { name: record.name }) });
              }}
            >
              {t('common.edit')}
            </BaseButton>


          </BaseSpace>
        );
      },
    },
  ];

  return (
    <>
      <Button
        style={{ marginBottom: '20px' }}
        onClick={() => {
          setTableData((prev) => ({ ...prev, loading: true }));
          setFilters({
            firstName: '',
            lastName: '',
            email: '',
            role: ''
          });

          setTimeout(() => {
            setPagination({ current: 1, pageSize: 20 });

          }, 1000);
        }}>Clear Filters</Button>
      {!tableData?.loading && <BaseTable
        columns={columns}
        dataSource={tableData.data}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: tableData.total,
        }}
        loading={tableData.loading}
        onChange={handleTableChange}
        scroll={{ x: 800 }}
        bordered
      />
      }
      <div className='d-block'>
        {tableData?.loading && <Spin spinning={tableData?.loading} />}
      </div>
    </>
  );
};
